/* istanbul ignore file */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { mapObject, constant, result } from 'underscore';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { MenuItem, Glyphicon } from '@bingads-webui-react/primitive';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withPreference } from '@bingads-webui-react/preference-context';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { SWITCH_BACK_NAV_REFRESH_KEY } from '@bingads-webui-react/preference-utility';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { getReturnMenuItemStyles } from '../../styles';

const UnthemedReturnMenuItem = ({
  classes,
  i18n,
  preferenceService,
  instrumentation,
  permissions,
}) => {
  const isNavRefresh = result(permissions.dynamic, 'NavigationRefresh', false);

  if (!isNavRefresh) {
    // Do not show switch back button if the user is not on the new UI
    return null;
  }

  const switchBackNavRefreshPreference = preferenceService.findByNameAtUserLevel(SWITCH_BACK_NAV_REFRESH_KEY);
  const switchBackNavRefresh = result(switchBackNavRefreshPreference, 'Data', false);

  const handleClick = (e) => {
    e.preventDefault();

    if (switchBackNavRefresh) {
      preferenceService.setAtUserLevel({
        Name: SWITCH_BACK_NAV_REFRESH_KEY,
        Data: false,
      }).then(({ Id }) => {
        preferenceService.onRemoteSyncDone(Id).then(() => {
          const scenarioSchema = {
            scenario: [{
              name: 'navigationRefresh2023',
              value: 'Navigation Refresh 2023',
              activity: [
                {
                  name: 'preferenceSet',
                  value: 'Set Preference using Preference Service',
                },
              ],
            }],
          };

          instrumentation.addScenario(scenarioSchema);
          instrumentation.navigationRefresh2023.create().preferenceSet.create().trace({
            preference: `${SWITCH_BACK_NAV_REFRESH_KEY} set to false from global menu with NavigationRefresh enabled`,
          });
          setClarityTag({ [SWITCH_BACK_NAV_REFRESH_KEY]: 'false' });

          window.location.reload();
        });
      });
    } else {
      window.location.reload();
    }
  };

  return (
    <div className={classes.returnMenuItemContainer}>
      <MenuItem
        className={classNames([classes.returnMenuItem, 'global-menu-tools-return'])}
        onClick={handleClick}
      >
        <Glyphicon
          className={classNames([classes.returnButtonIcon, 'global-menu-tools-return-glyph'])}
          glyph="ba-ReturnToSession"
        />
        <span className="global-menu-tools-return-click">
          {i18n.getString(_TL_('Switch back to the New Microsoft Advertising'))}
        </span>
      </MenuItem>
    </div>
  );
};

UnthemedReturnMenuItem.propTypes = {
  classes: PropTypes.shape(mapObject(
    getReturnMenuItemStyles({}, {}),
    constant(PropTypes.string.isRequired)
  )).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  preferenceService: PropTypes.shape({
    findByNameAtUserLevel: PropTypes.func,
    setAtUserLevel: PropTypes.func,
    onRemoteSyncDone: PropTypes.func,
  }),
  // eslint-disable-next-line react/forbid-prop-types
  instrumentation: PropTypes.object.isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      NavigationRefresh: PropTypes.bool,
    }),
  }).isRequired,
};

UnthemedReturnMenuItem.defaultProps = {
  preferenceService: null,
};

// eslint-disable-next-line
export const ReturnMenuItem = withInstrumentation(withPreference(withPermissions(withI18nConsumer(withDefaultStyles(UnthemedReturnMenuItem, getReturnMenuItemStyles)))));
